import React from 'react';
import useDialog from "../../../../hooks/useDialog";
import RadioGroupForm from "../../../../components/AttributeForm/RadioGroupForm";
import AttributeList from "../../../../components/AttributeList";
import {
    CANCELLATION_POLICIES,
    CANCELLATION_POLICIES_REGISTRY,
    TOURIST_TAX_POLICIES, TOURIST_TAX_POLICIES_REGISTRY
} from "../../../../services/booking_conditions/enums";
import EuroIcon from "@mui/icons-material/Euro";
import TextAttributeForm from "../../../../components/AttributeForm/TextAttributeForm";
import * as yup from "yup";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DateTimeAttributeForm from "../../../../components/AttributeForm/DateTimeAttributeForm";
import {useBookingUpdate} from "../../query";
import {dateAndTime, timeString} from "../../../../services/date/formatters";
import TimeAttributeForm from "../../../../components/AttributeForm/TimeAttributeForm";


const BookingConditionTab = ({booking}) => {
    const bookingCondition = booking.bookingCondition;

    const cancellationPolicy = bookingCondition?.cancellationPolicy;
    const touristTaxPolicy = bookingCondition?.touristTaxPolicy;
    const refundableUntil = bookingCondition?.refundableUntil;
    const touristTaxUnitPrice = bookingCondition?.touristTaxUnitPrice;
    const checkInFrom = bookingCondition?.checkInFrom;
    const checkOutBefore = bookingCondition?.checkOutBefore;

    const bookingUpdate = useBookingUpdate();

    const formCancellationPolicy = useDialog();
    const formTouristTaxPolicy = useDialog();
    const formRefundableUntil = useDialog();
    const formTouristTaxUnitPrice = useDialog();
    const formCheckInFrom = useDialog();
    const formCheckOutBefore = useDialog();

    const handleSubmit = async (form, data) => {
        form.setIsLoading(true);
        await bookingUpdate.mutateAsync({
            id: booking.id,
            data: {
                bookingConditionAttributes: {
                    id: bookingCondition?.id,
                    ...data
                }}
        })
        form.close();
        form.setIsLoading(false);
    }

    const attributes = [
        {
            label: "Politica di Cancellazione",
            value: CANCELLATION_POLICIES_REGISTRY.find(c => c.value === cancellationPolicy)?.label,
            defaultValue: "Non indicata",
            icon: <EventBusyIcon/>,
            formDialog: formCancellationPolicy,
            formComponent: <RadioGroupForm attribute="cancellationPolicy"
                                           label="Politica di Cancellazione"
                                           value={cancellationPolicy}
                                           options={CANCELLATION_POLICIES_REGISTRY.map(c => ({value: c.value, label: c.label}))}
                                           onClose={formCancellationPolicy.close}
                                           onSubmit={(data) => handleSubmit(formCancellationPolicy, data)}
                                           isLoading={formCancellationPolicy.loading}/>
        },
        {
            label: "Rimborsabile fino alla data",
            value: !!refundableUntil ? dateAndTime(refundableUntil) : null,
            defaultValue: cancellationPolicy === CANCELLATION_POLICIES.TIME_RESTRICTED_REFUNDABLE ? "Non indicata" : "Non applicabile",
            icon: <AccessTimeIcon/>,
            formDialog: formRefundableUntil,
            readOnly: cancellationPolicy !== CANCELLATION_POLICIES.TIME_RESTRICTED_REFUNDABLE,
            formComponent: <DateTimeAttributeForm attribute="refundableUntil"
                                                  label="Rimborsabile fino a"
                                                  type="date"
                                                  value={refundableUntil}
                                                  onClose={formRefundableUntil.close}
                                                  onSubmit={(data) => handleSubmit(formRefundableUntil, data)}
                                                  isLoading={formRefundableUntil.loading}/>
        },
        {
            label: "Politica Tassa di Soggiorno",
            value: TOURIST_TAX_POLICIES_REGISTRY.find(c => c.value === touristTaxPolicy)?.label,
            defaultValue: "Non indicata",
            icon: <EuroIcon/>,
            formDialog: formTouristTaxPolicy,
            formComponent: <RadioGroupForm attribute="touristTaxPolicy"
                                           label="Politica Tassa di Soggiorno"
                                           value={touristTaxPolicy}
                                           options={TOURIST_TAX_POLICIES_REGISTRY.map(c => ({value: c.value, label: c.label}))}
                                           onClose={formTouristTaxPolicy.close}
                                           onSubmit={(data) => handleSubmit(formTouristTaxPolicy, data)}
                                           isLoading={formTouristTaxPolicy.loading}/>
        },
        {
            label: "Importo tassa di soggiorno a notte per persona",
            value: touristTaxUnitPrice?.toFixed(2),
            icon: <EuroIcon/>,
            defaultValue: (touristTaxPolicy === TOURIST_TAX_POLICIES.INCLUDED || touristTaxPolicy === TOURIST_TAX_POLICIES.EXCLUDED) ? 'Non indicata' : 'Non applicabile',
            formDialog: formTouristTaxUnitPrice,
            readOnly: !(touristTaxPolicy === TOURIST_TAX_POLICIES.INCLUDED || touristTaxPolicy === TOURIST_TAX_POLICIES.EXCLUDED),
            formComponent: <TextAttributeForm attribute="touristTaxUnitPrice"
                                              label="Importo tassa di soggiorno a notte per persona"
                                              validationSchema={yup.object().shape({
                                                  touristTaxUnitPrice: yup.number()
                                                      .min(0, 'Deve essere maggiore o uguale a 0')
                                                      .required('Campo obbligatorio')
                                                      .typeError('Deve essere un numero')
                                              })}
                                              value={touristTaxUnitPrice}
                                              onClose={formTouristTaxUnitPrice.close}
                                              onSubmit={(data) => handleSubmit(formTouristTaxUnitPrice, data)}
                                              isLoading={formTouristTaxUnitPrice.loading}/>
        },
        {
            label: "Check-In aperto dalle ore",
            value: !!checkInFrom ? timeString(checkInFrom) : null,
            defaultValue: "Non indicato",
            icon: <AccessTimeIcon/>,
            formDialog: formCheckInFrom,
            formComponent: <TimeAttributeForm attribute="checkInFrom"
                                              label="Check-In da"
                                              type="time"
                                              minutesStep={15}
                                              value={checkInFrom}
                                              onClose={formCheckInFrom.close}
                                              onSubmit={(data) => handleSubmit(formCheckInFrom, data)}
                                              isLoading={formCheckInFrom.loading}/>
        },
        {
            label: "Check-Out entro le ore",
            value: !!checkOutBefore ? timeString(checkOutBefore) : null,
            defaultValue: "Non indicato",
            icon: <AccessTimeIcon/>,
            formDialog: formCheckOutBefore,
            formComponent: <TimeAttributeForm attribute="checkOutBefore"
                                              label="Check-Out entro"
                                              type="time"
                                              minutesStep={15}
                                              value={checkOutBefore}
                                              onClose={formCheckOutBefore.close}
                                              onSubmit={(data) => handleSubmit(formCheckOutBefore, data)}
                                              isLoading={formCheckOutBefore.loading}/>
        }
    ];

    return <AttributeList attributes={attributes}/>;
}


export default BookingConditionTab;